import * as React from "react";
import { Route, Switch, useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";

import type { PartialVehicleData } from "shared";

import { Cat4SearchBar } from "@/components/Header/Cat4SearchBar";
import { Restricted } from "@/components/Restricted";
import { SlidesStack } from "@/components/SlidesStack";
import { TabsIdentVehicle } from "@/components/TabsIdentVehicle";
import {
    emptyVehicleData,
    useVehicleData,
    VehicleDataProvider,
} from "@/context/VehicleDataContext";
import { selectionAlbumModeAtom } from "@/globalState/atoms";
import { useAPI } from "@/utils/hooks";

import { CarSelectorSearch } from "./CarSelectorSearch";
import { IframeCarSelector } from "./IframeCarSelector";
import { SelectBrand } from "./SelectBrand";
import { SelectModel } from "./SelectModel";
import { VehicleBanner } from "./VehicleBanner";

type Params = {
    ktype: string;
    brand: string;
    model: string;
    version: string;
};

const GetMidAutodata = () => {
    const { ktype } = useParams<Params>();
    const [, setVehicleData] = useVehicleData();
    const { data } = useAPI<{ mid: string }>(
        ktype ? `ktypnrToMid/${ktype}` : null
    );
    React.useEffect(() => {
        setVehicleData((vehicleData) => ({
            ...vehicleData,
            Mid_Autodata: data?.mid || " ",
        }));
    }, [data, setVehicleData]);
    return null;
};

const IdentVehicle = (): JSX.Element => {
    const { ktype, brand, model, version: hmdnr } = useParams<Params>();
    const albumMode = useRecoilValue(selectionAlbumModeAtom);
    const vehicleData: PartialVehicleData = {
        ...emptyVehicleData,
        ktype,
        hmd_nr: Number(hmdnr),
        Marque: brand,
        Modèle: model,
    };
    return (
        <VehicleDataProvider value={vehicleData}>
            <GetMidAutodata />
            <TabsIdentVehicle
                className="tabs-carselector"
                albumMode={albumMode}
                showIframe
            />
        </VehicleDataProvider>
    );
};

const CarSelector = (): JSX.Element => {
    const blocRef = React.useRef<HTMLDivElement>(null);
    const toggleFullscreen = (): void => {
        if (document.fullscreenElement === null) {
            blocRef.current && blocRef.current.requestFullscreen();
        } else {
            document.exitFullscreen();
        }
    };

    return (
        <Restricted access="CarSelector">
            <div className="inner-content" ref={blocRef}>
                <Cat4SearchBar />
                <SlidesStack className="oes-slider" />
                <Switch>
                    <Route path="/selection-vehicule/:vtype/:brand/:model/:version/:ktype">
                        <VehicleBanner toggleFullscreen={toggleFullscreen} />
                    </Route>
                    <Route
                        path={[
                            "/selection-vehicule",
                            "/selection-vehicule/:vtype",
                        ]}
                        exact
                    >
                        <CarSelectorSearch />
                    </Route>
                    <Route path="/selection-vehicule/app/:app" exact>
                        <CarSelectorSearch />
                    </Route>
                    <Route path="/selection-vehicule/:vtype/:make">
                        <CarSelectorSearch model />
                    </Route>
                </Switch>
                <Route
                    path={["/selection-vehicule", "/selection-vehicule/:vtype"]}
                    exact
                >
                    <SelectBrand />
                </Route>
                <Route path="/selection-vehicule/app/:app" exact>
                    <SelectBrand />
                </Route>
                <div className="with-short-bloc">
                    <Switch>
                        <Route path="/selection-vehicule/app/:app" exact />
                        <Route
                            path={[
                                "/selection-vehicule/:vtype/:brand",
                                "/selection-vehicule/:vtype/:brand/:model",
                            ]}
                            exact
                        >
                            <SelectModel />
                        </Route>
                    </Switch>
                    <Route path="/selection-vehicule/:vtype/:brand/:model/:version/:ktype">
                        <IdentVehicle />
                    </Route>
                </div>
            </div>
            <Switch>
                <Route path="/selection-vehicule/app/:app">
                    <IframeCarSelector fullscreen={false} isApp />
                </Route>
                <Route path="/selection-vehicule/:vtype/:brand/:model/:version/:ktype" />
                <Route path="/selection-vehicule">
                    <IframeCarSelector fullscreen={false} />
                </Route>
            </Switch>
        </Restricted>
    );
};

const MemoCarSelector = React.memo(CarSelector);
export { MemoCarSelector as CarSelector };
